/**
 * One tracker dog on screen (ROUND8 §3): a single rigid-skinned mesh on its own four-legged rig, texel-locked,
 * its heading snapped to the men's sixteen directions and its pose stepped "on twos" as theirs is (05 §6.2) -
 * so it moves beside its handler as one of the same drawings, not as something smoother pasted in.
 */

import { Bone, type Material, Skeleton, SkinnedMesh, Vector3 } from 'three';
import type { PixelCamera } from '../camera/PixelCamera';
import type { Palette } from '../palette';
import { buildDogGeometry } from './dogModel';
import { applyDogPose, type DogPoseInput } from './dogPoses';
import { D, DOG_BONE_COUNT, DOG_REST } from './dogRig';
import { FACING_STEPS, POSE_HZ } from './soldier';

export type { DogHead, DogMotion, DogPoseInput } from './dogPoses';

/** A one-shot's clock read on the pose grid, as the men's are. */
const onTwos = (seconds: number): number => Math.floor(seconds * POSE_HZ + 1e-6) / POSE_HZ;

/** Everything a pose needs, plus where it stands. */
export interface DogDrawState extends DogPoseInput {
  /** World position of the paws. */
  position: Vector3;
  /** Heading in radians; 0 faces south (toward the camera). */
  facing: number;
}

export class Dog {
  readonly mesh: SkinnedMesh;
  readonly state: DogDrawState = {
    position: new Vector3(), facing: 0, motion: 'stand', head: 'alert', travelled: 0, age: 0, sinceBark: -1,
  };
  private readonly bones: Bone[] = [];
  private readonly snapped = new Vector3();
  /** The state as posed: its clocks read on the pose grid. */
  private readonly stepped: DogPoseInput = { motion: 'stand', head: 'alert', travelled: 0, age: 0, sinceBark: -1 };
  private readonly boneOf = (i: number): Bone => this.bone(i);

  constructor(palette: Palette, material: Material) {
    for (let i = 0; i < DOG_BONE_COUNT; i++) {
      const bone = new Bone();
      const rest = DOG_REST[i];
      if (rest) {
        bone.position.set(rest[1], rest[2], rest[3]);
        const parent = this.bones[rest[0]];
        if (parent) parent.add(bone);
      }
      this.bones.push(bone);
    }
    const root = this.bone(D.root);
    root.updateMatrixWorld(true);
    this.mesh = new SkinnedMesh(buildDogGeometry(palette), material);
    this.mesh.add(root);
    this.mesh.bind(new Skeleton(this.bones));
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
  }

  private bone(index: number): Bone {
    const b = this.bones[index];
    if (!b) throw new Error(`no dog bone ${index}`);
    return b;
  }

  /** Apply state to the mesh. `time` is seconds; poses are evaluated on the POSE_HZ grid. */
  update(time: number, camera: PixelCamera): void {
    const s = this.state;
    camera.snapToTexel(s.position, this.snapped);
    this.mesh.position.copy(this.snapped);
    const step = (Math.PI * 2) / FACING_STEPS;
    this.mesh.rotation.y = Math.round(s.facing / step) * step;
    const p = this.stepped;
    p.motion = s.motion;
    p.head = s.head;
    p.travelled = s.travelled;
    p.age = onTwos(s.age);
    p.sinceBark = s.sinceBark < 0 ? s.sinceBark : onTwos(s.sinceBark);
    applyDogPose(this.bones, this.boneOf, p, onTwos(time));
  }

  /** Free what only this dog holds (the material is shared). */
  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.skeleton.dispose();
  }
}
